import { useCallback, useEffect, useRef, useState } from "react";

import {
	useProductStore,
	useProductStoreActions,
} from "@/modules/product/stores/product.store";
import type { PayloadSearch } from "@/modules/shared/types/payload-search.type";

import { useLayoutsStore } from "../stores/layouts.store";
import { useOrderFormComponentStore } from "../stores/order-form-component.store";
import { useOrderFormStore } from "../stores/order-form.store";
import { useProductSearchInputStore } from "../stores/product-search-input.store";
import type { TCreatePayloadToSearchProduct } from "../components/catalog/use-catalog.hook";
import { useOrderFormCatalogData } from "../components/catalog/catalog.data";

export function useProducts(options?: { skipInitialFetch?: boolean }) {
	const [currentPagePaginate, setCurrentPagePaginate] = useState(0);
	const [isLoadingPaginateProduct, setIsLoadingPaginateProduct] =
		useState(false);
	const {
		handleSubmitPaginateProduct,
		handleSubmitProduct,
		isLoadingRequestProduct,
	} = useOrderFormCatalogData();

	const { defaultFilter } = useProductSearchInputStore();

	const { products, productPaginate, currentPagination } = useProductStore();
	const {
		setIsActiveInfinityScroll,
		setCurrentPagination,
		clearProducts
	} = useProductStoreActions;

	const observerPaginationOfProduct = useRef<IntersectionObserver>(null);

	const { layoutType } = useLayoutsStore();

	const hasMoreProductInPagination =
		currentPagePaginate + 1 === productPaginate?.totalPaginas;

	const { jsonOrderForm } =
		useOrderFormStore();
	const { filterToCreatePaginationOfProduct } = useOrderFormComponentStore();


	const getDefaultFilter = () => {
		return defaultFilter === 'historico' ? 'historico' : 'produto.descricao';
	};

	const clearProductsList = useCallback(() => {
		clearProducts();
		setCurrentPagePaginate(0);
		setCurrentPagination(0);
		setIsLoadingPaginateProduct(false);

		if (observerPaginationOfProduct.current) {
			observerPaginationOfProduct.current.disconnect();
		}
	}, [clearProducts, setCurrentPagination]);

	const createPayloadToSearchProduct = ({
		filterAll,
		inputValueAll,
		payload,
	}: TCreatePayloadToSearchProduct) => {
		return {
			clientId: jsonOrderForm?.fkCliente,
			orderId: String(jsonOrderForm?.idPedidoCel),
			filter: filterAll ?? payload.payload?.filter,
			vigencia: jsonOrderForm?.fkVigencia,
			inputValue: inputValueAll ?? payload.payload?.inputValue,
			filterType: payload?.payload?.filterType ?? undefined,
			tableValueId: jsonOrderForm?.fkTabelaPreco,
			isControlProfitability: jsonOrderForm?.operacao?.controlaLucratividade,
			layoutType,
		};
	};

	const handleSubmitSearchProduct = async ({ payload }: PayloadSearch) => {
		const queryParams = new URLSearchParams();
		if (payload?.inputValue && payload?.filter) {
			queryParams.append("filter", payload.filter);
			queryParams.append("value", payload.inputValue);
		}

		const searchParams = queryParams.toString();

		const baseUrl = window.location.href.split("?")[0];

		if (searchParams === "") {
			window.history.pushState({ path: baseUrl }, "", baseUrl);
		} else {
			const newUrl = `${baseUrl}?${searchParams}`;

			window.history.pushState({ path: newUrl }, "", newUrl);
		}

		if (jsonOrderForm?.fkCliente === "") return;

		setCurrentPagination(0);
		setCurrentPagePaginate(0);

		if (payload.inputValue.length === 0) {
			await handleSubmitPaginateProduct({
				payload: createPayloadToSearchProduct({
					filterAll: getDefaultFilter(),
					inputValueAll: "",
				}),
			});
			return;
		}

		const newPayload = createPayloadToSearchProduct({
			payload: { payload },
		});

		handleSubmitPaginateProduct({
			payload: newPayload,
		});
	};

	const lastProductOfPaginationCallback = useCallback(
		(node) => {
			if (isLoadingRequestProduct) return;

			if (observerPaginationOfProduct.current)
				observerPaginationOfProduct.current.disconnect();

			observerPaginationOfProduct.current = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting && !hasMoreProductInPagination) {
						setCurrentPagePaginate((prev) => prev + 1);
						setIsLoadingPaginateProduct(true);
					}
				},
			);

			if (node) observerPaginationOfProduct.current.observe(node);
		},
		[isLoadingRequestProduct, hasMoreProductInPagination],
	);

	useEffect(() => {
		// Evitar busca inicial quando o chamador sinaliza para pular
		if (options?.skipInitialFetch) return;

		handleSubmitSearchProduct({
			payload: createPayloadToSearchProduct({
				filterAll: getDefaultFilter(),
				inputValueAll: "",
			}),
		});



		return () => {
			if (observerPaginationOfProduct.current) {
				observerPaginationOfProduct.current.disconnect();
			}
		};
	}, []);

	useEffect(() => {
		if (isLoadingPaginateProduct) {
			setIsActiveInfinityScroll(true);

			const payload = {
				jsonOrderForm,
				arrayOfProducts: productPaginate?.pagina[currentPagePaginate],
				filter: filterToCreatePaginationOfProduct,
			};

			handleSubmitProduct({ payload });
			setIsLoadingPaginateProduct(false);
		}

		setCurrentPagination(currentPagePaginate);
	}, [currentPagePaginate]);

	return {
		products,
		isLoadingProducts: isLoadingRequestProduct || isLoadingPaginateProduct,
		lastProductOfPaginationCallback,
		handleSubmitSearchProduct,
		clearProductsList,
	};
}